import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { ColumnProps } from "antd/lib/table";
import { Button } from "Components/General/Button";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { MetaDataForm } from "./MetaDataForm";
import { Space } from "antd";
import { ImportMetadata } from "../Components/ImportMetadata";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { download } from "utils/download";
import { metadataStore, MetadataViewMode } from "Pages/Admin/Stores/MetadataStore";
import { projectsDataStore, userInfoStore } from "Stores";
import classnames from "classnames";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import { Table } from "Components/General/Table";

interface MetadataProps extends WithTranslation, WithRouterProps {
  mode?: MetadataViewMode;
}

interface MetadataState {
  exportLoading: boolean;
  projectId: number;
}

const MODAL_PROPS = { width: `100%`, style: { maxWidth: 1100 }, footer: null, destroyOnHidden: true };

export const Metadata = withTranslation(I18N_NS.ADMIN)(
  withRouter(
    observer(
      class extends React.Component<MetadataProps, MetadataState> {
        readonly state: MetadataState = {
          exportLoading: false,
          projectId: null,
        };

        componentDidMount() {
          this.setState({ projectId: +(this.props.params?.id || projectsDataStore.projectId) }, () => {
            this.fetch();
          });
        }

        componentWillUnmount() {
          metadataStore.clear();
        }

        handleEdit = async (obj: API_Metadata) => {
          await metadataStore.edit(obj);
        };

        createMetadata = async (obj: API_Metadata) => {
          await metadataStore.save(obj);
        };

        fetch = async () => {
          metadataStore.setMode(this.props.mode);
          await metadataStore.fetch(this.state.projectId);
        };

        exportMetadata = async () => {
          try {
            this.setState({ exportLoading: true });
            const file = await API.getBlob(API_URL.METADATA.getAllMetadataAsExcel(this.state.projectId));
            download(file, file.name);
          } catch (error) {
            throw error;
          } finally {
            this.setState({ exportLoading: false });
          }
        };

        render() {
          const { t } = this.props;
          const { exportLoading } = this.state;

          const basicColumns: ColumnProps<API_Metadata>[] = [
            {
              dataIndex: "definitionId",
              title: "ID",
            },
            {
              dataIndex: "key",
              title: t("Name"),
            },
            {
              dataIndex: "description",
              title: t("Description"),
            },
          ];

          const adminColumns: ColumnProps<API_Metadata>[] = [
            {
              dataIndex: "format",
              title: t("Format"),
            },
            {
              dataIndex: "type",
              title: t("Type"),
              render: (text, record) => (record.type === 0 ? t("Manual") : record.type === 1 ? t("Automatic") : ""),
            },
            {
              dataIndex: "required",
              title: t("Required"),
              render: (text, record) => (record.required ? t("Yes") : t("No")),
            },
            {
              dataIndex: "active",
              title: t("Active"),
              render: (text, record) => (record.active ? t("Yes") : t("No")),
            },
          ];

          const actionColumn: ColumnProps<API_Metadata>[] = [
            {
              dataIndex: "definitionId",
              title: t("Actions"),
              render: (text, record) => {
                const isViewOnly = metadataStore.mode === "SETTINGS" && record.sysMetadata && !userInfoStore.isSysAdmin;
                return (
                  <ModalConsumer>
                    {({ showModal }) => (
                      <a
                        onClick={() => {
                          showModal(MetaDataForm, {
                            modalProps: {
                              ...MODAL_PROPS,
                              title: t(isViewOnly ? "View metadata" : "Edit metadata"),
                            },
                            componentProps: {
                              onEdit: this.handleEdit,
                              onCreate: this.createMetadata,
                              initialData: record,
                              readOnly: isViewOnly,
                            },
                          });
                        }}
                      >
                        {t(isViewOnly ? "View" : "Edit")}
                      </a>
                    )}
                  </ModalConsumer>
                );
              },
            },
          ];

          const columns: ColumnProps<API_Metadata>[] =
            metadataStore.mode === "ADMIN"
              ? [...basicColumns, ...adminColumns, ...actionColumn]
              : [...basicColumns, ...actionColumn];

          return (
            <div className={classnames({ "g-card": metadataStore.mode === "ADMIN" })}>
              {metadataStore.mode === "ADMIN" && (
                <>
                  <h1>{t("Manage metadata")}</h1>
                  <ModalConsumer>
                    {({ showModal }) => (
                      <div className="text-right">
                        <Space>
                          <Button
                            onClick={() =>
                              showModal(ImportMetadata, {
                                modalProps: { ...MODAL_PROPS, title: t("Import...") },
                                componentProps: { projectId: this.state.projectId, afterSuccess: this.fetch },
                              })
                            }
                          >
                            {t("Import...")}
                          </Button>
                          <Button onClick={this.exportMetadata} loading={exportLoading}>
                            {t("Export as Excel")}
                          </Button>
                          <Button
                            type="primary"
                            faIcon={"plus"}
                            onClick={() =>
                              showModal(MetaDataForm, {
                                modalProps: { ...MODAL_PROPS, title: t("Add metadata") },
                                componentProps: {
                                  onEdit: this.handleEdit,
                                  onCreate: this.createMetadata,
                                },
                              })
                            }
                          >
                            {t("Add metadata")}
                          </Button>
                        </Space>
                      </div>
                    )}
                  </ModalConsumer>
                </>
              )}
              <Table<API_Metadata>
                name="adminMetadata"
                columns={columns}
                dataSource={metadataStore.data}
                loading={metadataStore.state === "LOADING"}
                rowKey="definitionId"
                pagination={{ hideOnSinglePage: true }}
              />
            </div>
          );
        }
      }
    )
  )
);
